import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { CheckCircle, SyncProblem } from "@mui/icons-material";

/**
 * Blocking alert shown when the last several events were all tagged at the
 * same video time as the latest synced timestamp — a sign the video device
 * has stopped publishing (e.g. lost internet) while the realtime link itself
 * still looks healthy. Closes on its own once a new timestamp arrives.
 */
const VideoTimeFrozenAlert = ({ open, videoTime, eventCount, onConfirmInSync }) => (
  <Dialog
    open={!!open}
    disableEscapeKeyDown
    PaperProps={{
      sx: {
        borderRadius: "12px",
        minWidth: 360,
        maxWidth: "92vw",
        border: "1px solid var(--border-1)",
      },
    }}
  >
    <DialogTitle
      sx={{
        backgroundColor: "rgba(180, 83, 9, 0.08)",
        borderBottom: "2px solid rgba(180, 83, 9, 1)",
        py: 1.5,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <SyncProblem sx={{ fontSize: 18, color: "rgba(180, 83, 9, 1)" }} />
        <Typography
          sx={{ fontSize: "15px", fontWeight: 700, color: "rgba(180, 83, 9, 1)" }}
        >
          Video time is not updating
        </Typography>
      </Stack>
    </DialogTitle>
    <DialogContent sx={{ py: 2 }}>
      <Typography sx={{ fontSize: "13px", color: "var(--text-2)", mt: 1 }}>
        The last {eventCount} events were all tagged at the same video time.
        Please check the internet connection of the device playing the video.
      </Typography>
      <Box
        sx={{
          my: 2,
          py: 1.5,
          borderRadius: "8px",
          textAlign: "center",
          border: "1px solid var(--border-1)",
        }}
      >
        <Typography sx={{ fontSize: "12px", color: "var(--text-2)" }}>
          Latest video time received
        </Typography>
        <Typography sx={{ fontSize: "24px", fontWeight: 700, letterSpacing: 1 }}>
          {videoTime}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: "13px", color: "var(--text-2)" }}>
        Compare this time with the video player. If they match, continue
        tagging. If not, fix the connection on the video device — this alert
        closes on its own once a new time arrives.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 2, pb: 2 }}>
      <Button
        variant="contained"
        size="small"
        onClick={onConfirmInSync}
        startIcon={<CheckCircle sx={{ fontSize: "16px !important" }} />}
        sx={{
          textTransform: "none",
          borderRadius: "8px",
          fontSize: "12px",
          fontWeight: 700,
          backgroundColor: "rgba(180, 83, 9, 1)",
          "&:hover": { backgroundColor: "rgba(146, 64, 14, 1)" },
        }}
      >
        It matches the video player
      </Button>
    </DialogActions>
  </Dialog>
);

export default VideoTimeFrozenAlert;
